import { SESSIONS_API_BASE, apiFetch } from '../config/apiBase';
/**
 * Subscription Service
 * API helpers for subscription management
 */

const BASE = SESSIONS_API_BASE;

/**
 * Get active subscription for a client
 * @param {string} clientId
 * @param {string} authToken - Firebase auth token (Bearer token)
 * @returns {Promise<{
 *   id: string,
 *   client_id: string,
 *   entitlement_id: string,
 *   plan_id: string,
 *   billing_cycle: string,
 *   status: string,
 *   current_period_start: string,
 *   current_period_end: string,
 *   cancel_at_period_end: boolean,
 *   grace_period_ends_at: string,
 *   stripe_subscription_id: string,
 *   stripe_customer_id: string,
 *   stripe_price_id: string,
 *   created_at: string,
 *   updated_at: string
 * }>}
 */
export async function getActiveSubscription(clientId, authToken) {
  const response = await apiFetch(`${BASE}/admin/clients/${clientId}/subscription`, {
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });

  if (!response.ok) {
    if (response.status === 404) {
      return null; // No active subscription
    }
    throw new Error(`Failed to fetch subscription: ${response.statusText}`);
  }

  const json = await response.json();
  // Handle nested data structure: {success: true, data: {...}}
  return json.data || json;
}

/**
 * Cancel a subscription
 * @param {string} clientId
 * @param {string} authToken - Firebase auth token (Bearer token)
 * @param {boolean} cancelImmediately - If true, cancel immediately. Otherwise cancel at period end.
 * @returns {Promise<void>}
 */
export async function cancelSubscription(clientId, authToken, cancelImmediately = false) {
  const response = await apiFetch(`${BASE}/admin/clients/${clientId}/subscription/cancel`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ cancel_immediately: cancelImmediately })
  });

  if (!response.ok) {
    throw new Error(`Failed to cancel subscription: ${response.statusText}`);
  }

  const json = await response.json();
  // Handle nested data structure: {success: true, data: {...}}
  return json.data || json;
}

/**
 * Get payment method for a subscription
 * @param {string} clientId
 * @param {string} authToken - Firebase auth token (Bearer token)
 * @returns {Promise<{
 *   payment_method: {
 *     id: string,
 *     type: string,
 *     card: {
 *       brand: string,
 *       last4: string,
 *       exp_month: number,
 *       exp_year: number
 *     }
 *   }
 * }>}
 */
export async function getPaymentMethod(clientId, authToken) {
  const response = await apiFetch(`${BASE}/admin/clients/${clientId}/subscription/payment-method`, {
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });

  if (!response.ok) {
    if (response.status === 404) {
      return null; // No payment method
    }
    throw new Error(`Failed to fetch payment method: ${response.statusText}`);
  }

  const json = await response.json();
  // Handle nested data structure: {success: true, data: {...}}
  return json.data || json;
}

/**
 * Get invoices for a subscription
 * @param {string} clientId
 * @param {string} authToken - Firebase auth token (Bearer token)
 * @param {number} limit - Number of invoices to retrieve (1-100, default 10)
 * @param {string} startingAfter - Pagination cursor
 * @returns {Promise<{
 *   invoices: Array<{
 *     id: string,
 *     number: string,
 *     amount_due: number,
 *     amount_paid: number,
 *     currency: string,
 *     status: string,
 *     created: number,
 *     hosted_invoice_url: string,
 *     invoice_pdf: string
 *   }>,
 *   has_more: boolean,
 *   next_starting_after: string | null
 * }>}
 */
export async function getInvoices(clientId, authToken, limit = 10, startingAfter = null) {
  const params = new URLSearchParams({ limit: limit.toString() });
  if (startingAfter) {
    params.append('starting_after', startingAfter);
  }

  const response = await apiFetch(`${BASE}/admin/clients/${clientId}/subscription/invoices?${params}`, {
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch invoices: ${response.statusText}`);
  }

  const json = await response.json();
  // Handle nested data structure: {success: true, data: {...}}
  return json.data || json;
}

/**
 * Change subscription plan
 * @param {string} clientId
 * @param {string} authToken - Firebase auth token (Bearer token)
 * @param {string} newPlanId - New plan ID (TIER_2 or TIER_3)
 * @param {string} newBillingCycle - New billing cycle (MONTHLY or ANNUAL)
 * @returns {Promise<void>}
 */
export async function changeSubscriptionPlan(clientId, authToken, newPlanId, newBillingCycle) {
  const response = await apiFetch(`${BASE}/admin/clients/${clientId}/subscription/change-plan`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({
      new_plan_id: newPlanId,
      new_billing_cycle: newBillingCycle
    })
  });

  if (!response.ok) {
    throw new Error(`Failed to change plan: ${response.statusText}`);
  }

  const json = await response.json();
  // Handle nested data structure: {success: true, data: {...}}
  return json.data || json;
}

export default {
  getActiveSubscription,
  cancelSubscription,
  getPaymentMethod,
  getInvoices,
  changeSubscriptionPlan
};
